import { NOVALA_FIELDS, VALID_ACCOUNT_TYPES, FIELD_SYNONYMS } from '../pages/Accounting/ChartOfAccounts/components/ImportChartOfAccountsModal/constants';

export interface ParsedRow { rowNum: number; values: string[]; }
export interface ParsedFile { headers: string[]; rows: ParsedRow[]; fileName: string; fileSize: number; }
export type RowStatus = 'ready' | 'duplicate' | 'error';
export interface ValidatedRow {
  rowNum: number;
  status: RowStatus;
  errorMessage?: string;
  name: string;
  number?: string;
  accountType: string;
  detailType?: string;
  description?: string;
  novalaBalance?: number;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_ROWS = 1000;

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (c === ',' && !inQuotes) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out;
}

function parseCsv(text: string): { headers: string[]; rows: ParsedRow[] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]);
  const rows: ParsedRow[] = lines.slice(1, MAX_ROWS + 1).map((line, idx) => ({
    rowNum: idx + 2,
    values: parseCsvLine(line),
  }));
  return { headers, rows };
}

export function parseFile(file: File): Promise<ParsedFile> {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_FILE_SIZE) { reject(new Error('File is too large (max 5 MB).')); return; }
    if (!file.name.toLowerCase().endsWith('.csv')) { reject(new Error('Only CSV files are supported.')); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseCsv(String(e.target?.result || ''));
        resolve({ ...parsed, fileName: file.name, fileSize: file.size });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsText(file);
  });
}

export function autoMatchColumns(headers: string[]): string[] {
  const used = new Set<string>();
  return headers.map((h) => {
    const key = h.trim().toLowerCase();
    const match = FIELD_SYNONYMS[key];
    if (match && !used.has(match)) { used.add(match); return match; }
    return 'ignore';
  });
}

export function validateRows(parsed: ParsedFile, mapping: string[], existingAccountNames: string[]): ValidatedRow[] {
  const idxOf = (f: string) => mapping.indexOf(f);
  const nameIdx = idxOf('name');
  const typeIdx = idxOf('accountType');
  const numberIdx = idxOf('number');
  const detailIdx = idxOf('detailType');
  const descIdx = idxOf('description');
  const balanceIdx = idxOf('novalaBalance');
  const existingSet = new Set(existingAccountNames.map((n) => n.trim().toLowerCase()));
  const seen = new Set<string>();

  return parsed.rows.map((row) => {
    const name = nameIdx >= 0 ? (row.values[nameIdx] || '').trim() : '';
    const accountType = typeIdx >= 0 ? (row.values[typeIdx] || '').trim() : '';
    const number = numberIdx >= 0 ? (row.values[numberIdx] || '').trim() || undefined : undefined;
    const detailType = detailIdx >= 0 ? (row.values[detailIdx] || '').trim() || undefined : undefined;
    const description = descIdx >= 0 ? (row.values[descIdx] || '').trim() || undefined : undefined;
    const balanceRaw = balanceIdx >= 0 ? (row.values[balanceIdx] || '').trim() : '';
    const balanceNum = balanceRaw ? parseFloat(balanceRaw.replace(/[^0-9.-]/g, '')) : NaN;
    const novalaBalance = !isNaN(balanceNum) ? balanceNum : undefined;

    const base: ValidatedRow = { rowNum: row.rowNum, status: 'ready', name, number, accountType, detailType, description, novalaBalance };

    if (!name) return { ...base, status: 'error', errorMessage: 'Account name is required.' };
    if (!accountType) return { ...base, status: 'error', errorMessage: 'Account type is required.' };
    const typeMatch = VALID_ACCOUNT_TYPES.find((t) => t.toLowerCase() === accountType.toLowerCase());
    if (!typeMatch) return { ...base, status: 'error', errorMessage: `"${accountType}" is not a valid Account type.` };
    base.accountType = typeMatch;

    const nameLower = name.toLowerCase();
    if (existingSet.has(nameLower) || seen.has(nameLower)) {
      return { ...base, status: 'duplicate', errorMessage: 'Already exists in your Chart of accounts.' };
    }
    seen.add(nameLower);
    return base;
  });
}

export function downloadTemplate(): void {
  const headers = ['Account name', 'Account number', 'Account type', 'Detail type', 'Description', 'Novala balance'];
  const sample = [
    ['Business Chequing', '1010', 'Bank', 'Chequing', 'Main business account', '12480.52'],
    ['Office Supplies', '5010', 'Expenses', 'Office/general administrative expenses', '', ''],
  ];
  const csv = [headers, ...sample].map((row) => row.map((c) => (c.includes(',') ? `"${c}"` : c)).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'novala-chart-of-accounts-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function useImportChartOfAccounts() {
  return { parseFile, autoMatchColumns, validateRows, downloadTemplate };
}